import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Collapse, LinearProgress, Typography } from '@mui/material';
import { RUN_KIND_LABELS, RUN_STATUS_COLORS, RUN_STATUS_LABELS, SCOPE_LABELS, TRIGGER_LABELS } from '../constants';
import { TimesheetRun } from '../types';
import { fmtMoment, isRunProblem, percent, runTotals } from '../utils';

interface Props {
  run: TimesheetRun | null;
  loaded: boolean;
  cancelling: boolean;
  onCancel: () => void;
}

/** Последний запуск массового создания: статус, ход, итог и список ошибок по аптекам */
const RunPanel: React.FC<Props> = ({ run, loaded, cancelling, onCancel }) => {
  const [errorsOpen, setErrorsOpen] = useState(false);

  if (!loaded) return <Typography variant="body2" color="text.secondary">Загрузка…</Typography>;
  if (!run) return <Typography variant="body2" color="text.secondary">Запусков ещё не было</Typography>;

  const running = run.status === 'running';
  const done = percent(run.processed, run.total);

  return (
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5, bgcolor: '#f8fafc' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>Запуск №{run.id}</Typography>
        <Chip size="small" color={RUN_STATUS_COLORS[run.status]} variant="outlined" label={RUN_STATUS_LABELS[run.status]} />
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {fmtMoment(run.startedAt)}
          {` · ${TRIGGER_LABELS[run.triggerType] || run.triggerType}`}
          {run.startedBy ? ` · ${run.startedBy}` : ''}
          {SCOPE_LABELS[run.scope] ? ` · ${SCOPE_LABELS[run.scope]}` : ''}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {running && (
          <Button
            size="small" color="warning" variant="outlined" disabled={cancelling} onClick={onCancel}
            startIcon={cancelling ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
          >
            Остановить
          </Button>
        )}
      </Box>

      <Typography variant="body2" sx={{ mb: 0.75, color: '#475569' }}>
        {RUN_KIND_LABELS[run.kind] || run.kind}, {run.year} год
        {run.reason ? <Box component="span" sx={{ color: '#64748b' }}>{` · Причина: ${run.reason}`}</Box> : null}
      </Typography>

      <LinearProgress
        variant="determinate" value={done}
        color={isRunProblem(run.status) ? 'error' : 'primary'}
        sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0' }}
      />
      <Typography variant="body2" sx={{ mt: 0.75, color: '#475569' }}>
        Обработано {run.processed.toLocaleString('ru-RU')} из {run.total.toLocaleString('ru-RU')}: {runTotals(run)}
      </Typography>
      {run.message && !running && (
        <Typography variant="body2" sx={{ mt: 0.5, color: isRunProblem(run.status) ? 'error.main' : '#475569' }}>{run.message}</Typography>
      )}

      {run.errors.length > 0 && (
        <>
          <Typography
            variant="body2" onClick={() => setErrorsOpen(o => !o)}
            sx={{ mt: 0.75, color: 'primary.main', cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
          >
            {errorsOpen ? 'Скрыть ошибки' : `Показать ошибки (${run.errors.length})`}
          </Typography>
          <Collapse in={errorsOpen} unmountOnExit>
            <Box sx={{ mt: 0.5, maxHeight: 160, overflowY: 'auto' }}>
              {run.errors.map(e => (
                <Typography key={`${e.pharmacyNum}-${e.message}`} variant="caption" sx={{ display: 'block', color: '#b91c1c' }}>
                  Аптека №{e.pharmacyNum}: {e.message}
                </Typography>
              ))}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default React.memo(RunPanel);
